import {
  Component,
  ComponentRef,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AppendComponentService } from '../../services/append-component.service';
import { TemplateComponent } from '../template/template.component';
import { DOMService } from '../../services/dom.service';
import { DropdownService } from './dropdown.service';

let id = 0;

@Component({
  selector: 'wfm-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  @Input() maxHeight: number;
  @Input() isOpen = false;
  @Input() disabled = false;
  @Input() forceOpen = false;
  @Input() forceClose = false;
  @Input() scrollbar = false;
  @Input() adjustVerticalOverflow = false;
  @Input() closeOnMousedown: () => boolean;
  @Input() topPosition = 0;

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() isOutsideMousedown = new EventEmitter<boolean>();

  private $defaultPosition = 'left';
  @Input() set defaultPosition(position: string) {
    this.$defaultPosition = ['left', 'right'].find(p => p === position.toLowerCase()) || 'left';
  }
  get defaultPosition(): string {
    return this.$defaultPosition;
  }

  @Input() dropdownWidth: string;
  @Input() maxWidth: string;

  @ViewChild('dropdown') dropdown: ElementRef;
  @ViewChild('dropdownContent') dropdownContent: ElementRef;
  @ViewChild('dropdownContentTemplate') dropdownContentTemplate: TemplateRef<any>;

  darkTheme = false;

  private contentRef: ComponentRef<TemplateComponent>;

  private window: Window;

  public id = 0;

  getStyle = (content: HTMLElement): any => {
    let width = '';
    let maxWidth = '';
    let maxCalculatedHeight = '';
    let display = 'none';

    const triggerRect = this.domService.getRect(this.dropdown && this.dropdown.nativeElement);

    if (content) {
      // set content to configured width
      display = 'flex';
      width = content.style['min-width'] = this.dropdownWidth || '';
      maxWidth = content.style['max-width'] = this.maxWidth || '';

      // reset position to its original position. So current applyed position do not affect position calculation
      content.style.left = content.style.right = '';
      content.style.display = display;

      const elementWidth = content.offsetWidth;

      let top = triggerRect.top + triggerRect.height + this.topPosition;
      const maxAvailableHeight = this.window.innerHeight - top;

      if (!this.adjustVerticalOverflow) {
        maxCalculatedHeight = this.maxHeight ? `${this.maxHeight}px` : `${maxAvailableHeight}px`;
      } else {
        if (maxAvailableHeight < content.offsetHeight) {
          top = top - (content.offsetHeight - maxAvailableHeight);
          top = top < 0 ? 0 : top;
        }
      }
      content.style.top = top + 'px';

      let overflow: number;

      if (this.$defaultPosition === 'left') {
        overflow = triggerRect.left + elementWidth - this.window.innerWidth;

        content.style.left = triggerRect.left + 'px';

        if (overflow > 0) {
          content.style.left = '';
          content.style.right = '0px';
        }

        if (triggerRect.left < 0) {
          content.style.left = '0px';
          content.style.right = '';
        }
      } else {
        overflow = elementWidth - (triggerRect.left + triggerRect.width);

        content.style.right = this.window.innerWidth - (triggerRect.left + triggerRect.width) + 'px';

        if (overflow > 0) {
          content.style.right = '';
          content.style.left = '0px';
        }

        if (triggerRect.left + triggerRect.width > this.window.innerWidth) {
          content.style.right = '0px';
          content.style.left = '';
        }
      }
    }

    return {
      display, // Needs flex so content can become scrollable if max height reached
      'min-width': width,
      'max-height': maxCalculatedHeight,
      'max-width': maxWidth,
      top: (content && content.style.top) || '',
      right: (content && content.style.right) || '',
      left: (content && content.style.left) || ''
    };
  };

  constructor(
    @Inject(DOCUMENT) private document,
    private appendComponent: AppendComponentService,
    private domService: DOMService,
    private dropdownService: DropdownService,
    public element: ElementRef
  ) {
    this.window = this.document.defaultView;
    this.id = ++id;
  }

  ngOnInit(): void {
    this.dropdownService.add(this);
  }

  ngOnDestroy(): void {
    this.closeDropdown();
    this.dropdownService.remove(this);
  }

  ngDoCheck(): void {
    this.darkTheme = !!(this.element.nativeElement as Element).closest('.dark-theme');
  }

  ignoreNextMousedown = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isOpen && changes.isOpen.currentValue !== changes.isOpen.previousValue) {
      if (!this.isOpen) {
        this.closeDropdown();

        // notify children dropdowns to close
        this.dropdownContent && this.dropdownService.outsideMousedown(this.dropdownContent.nativeElement);
      }
      if (this.isOpen) {
        this.ignoreNextMousedown = true;
        this.openDropdown();
        this.window.setTimeout(() => {
          this.ignoreNextMousedown = false;
        }, 250);
      }
    }
  }

  triggerClick(): void {
    if (this.forceClose) {
      this.isOpen && this.closeDropdown();
      this.isOpen = false;
      this.isOpenChange.emit(this.isOpen);
    } else if (!this.forceOpen) {
      this.isOpen = !this.disabled && !this.isOpen;
      this.isOpen && this.openDropdown();
      !this.isOpen && this.closeDropdown();
      this.isOpenChange.emit(this.isOpen);
    }
  }

  contentMousedown(): void {
    this.ignoreNextMousedown = true;

    // notify parent dropdowns to do not close
    this.dropdownService.contentMousedown(this.element.nativeElement);
  }

  outsideMousedown(): void {
    if (this.ignoreNextMousedown) {
      this.ignoreNextMousedown = false;
      return;
    }

    if (!this.isOpen) return;

    if (this.closeOnMousedown && !this.closeOnMousedown()) return;

    this.isOpen = false;

    // notify children dropdowns to close
    this.dropdownContent && this.dropdownService.outsideMousedown(this.dropdownContent.nativeElement);

    this.isOpenChange.emit(this.isOpen);
    this.isOutsideMousedown.emit(this.isOpen);
    this.closeDropdown();
  }

  private openDropdown() {
    if (!this.contentRef) {
      this.contentRef = this.appendComponent.appendComponent(TemplateComponent);
      this.contentRef.instance.template = this.dropdownContentTemplate;
      this.contentRef.instance.detectChanges();
    }
  }

  private closeDropdown() {
    this.contentRef && this.appendComponent.removeComponent(this.contentRef);
    this.contentRef = undefined;
  }
}
